import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { ContractAmbiguityToolbar } from './ContractAmbiguityToolbar';
import { ContractAmbiguitySectionList } from './ContractAmbiguitySectionList';
import { ContractAmbiguityFlagsList } from './ContractAmbiguityFlagsList';
import {
  runContractAmbiguityAnalysis,
  fetchContractAmbiguityFindings,
  type AmbiguityFinding,
} from '../../services/api';

interface ContractAmbiguityAnalysisPanelProps {
  contractId: string;
}

export const ContractAmbiguityAnalysisPanel: React.FC<ContractAmbiguityAnalysisPanelProps> = ({
  contractId,
}) => {
  const [findings, setFindings] = useState<AmbiguityFinding[]>([]);
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | null>(null);
  const [filters, setFilters] = useState({ severity: '', flagType: '', keyword: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing findings on mount
  useEffect(() => {
    const loadFindings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchContractAmbiguityFindings(contractId);
        setFindings(
          result.findings.map((f: any) => ({
            id: f.id,
            clauseId: f.clause_id || f.clauseId,
            clauseText: f.clause_text || f.clauseText,
            articleNumber: f.article_number || f.articleNumber,
            sectionNumber: f.section_number || f.sectionNumber,
            sectionTitle: f.section_title || f.sectionTitle || '',
            ruleId: f.rule_id || f.ruleId,
            ruleName: f.rule_name || f.ruleName,
            flagType: f.flag_type || f.flagType,
            severity: f.severity,
            matchText: f.match_text || f.matchText,
            charOffsetStart: f.char_offset_start ?? f.charOffsetStart,
            charOffsetEnd: f.char_offset_end ?? f.charOffsetEnd,
            rationale: f.rationale,
          }))
        );
      } catch (err) {
        console.log('[ContractAmbiguity] No existing findings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFindings();
  }, [contractId]);

  const handleRunAnalysis = useCallback(async () => {
    setIsRunning(true);
    setError(null);
    try {
      const result = await runContractAmbiguityAnalysis(contractId);
      setFindings(
        result.findings.map((f: any) => ({
          id: f.id,
          clauseId: f.clauseId,
          clauseText: f.clauseText,
          articleNumber: f.articleNumber,
          sectionNumber: f.sectionNumber,
          sectionTitle: f.sectionTitle || '',
          ruleId: f.ruleId,
          ruleName: f.ruleName,
          flagType: f.flagType,
          severity: f.severity,
          matchText: f.matchText,
          charOffsetStart: f.charOffsetStart,
          charOffsetEnd: f.charOffsetEnd,
          rationale: f.rationale,
        }))
      );
      setSelectedSectionKey(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsRunning(false);
    }
  }, [contractId]);

  // Client-side filtering
  const filteredFindings = useMemo(() => {
    let result = findings;

    if (filters.severity) {
      result = result.filter((f) => f.severity === filters.severity);
    }
    if (filters.flagType) {
      result = result.filter((f) => f.flagType === filters.flagType);
    }
    if (filters.keyword) {
      const lower = filters.keyword.toLowerCase();
      result = result.filter(
        (f) =>
          f.clauseText.toLowerCase().includes(lower) ||
          f.rationale.toLowerCase().includes(lower) ||
          f.ruleName.toLowerCase().includes(lower)
      );
    }

    return result;
  }, [findings, filters]);

  return (
    <div className="space-y-4" data-testid="contract-ambiguity-panel">
      {/* Toolbar */}
      <ContractAmbiguityToolbar
        filters={filters}
        onFilterChange={setFilters}
        onRunAnalysis={handleRunAnalysis}
        isRunning={isRunning}
        hasFindings={findings.length > 0}
      />

      {/* Summary stats */}
      {findings.length > 0 && (
        <div className="flex items-center gap-4 text-sm text-gray-600" data-testid="ambiguity-stats">
          <span>{filteredFindings.length} finding{filteredFindings.length !== 1 ? 's' : ''}</span>
          <span className="text-red-600">
            {filteredFindings.filter((f) => f.severity === 'high').length} high
          </span>
          <span className="text-orange-600">
            {filteredFindings.filter((f) => f.severity === 'medium').length} medium
          </span>
          <span className="text-blue-600">
            {filteredFindings.filter((f) => f.severity === 'low').length} low
          </span>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div
          className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          data-testid="ambiguity-error"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-8 text-gray-400">
          Loading existing findings...
        </div>
      )}

      {/* Main content: section list + findings */}
      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left sidebar: section navigation */}
          {findings.length > 0 && (
            <div className="lg:col-span-1">
              <ContractAmbiguitySectionList
                findings={filteredFindings}
                selectedSectionKey={selectedSectionKey}
                onSelectSection={setSelectedSectionKey}
              />
            </div>
          )}

          {/* Right: findings list */}
          <div className={findings.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4'}>
            <ContractAmbiguityFlagsList
              findings={filteredFindings}
              selectedSectionKey={selectedSectionKey}
            />
          </div>
        </div>
      )}
    </div>
  );
};
